import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Clock, FileText, ShieldCheck } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError, WHATSAPP_LINK } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { CONDITIONS } from "@/data/care";

const COUNTRIES = ["India","Canada","United States","United Kingdom","Australia","United Arab Emirates","Saudi Arabia","Other"];

// Country-differentiated pricing for paid-media landing (via ?ref=)
const PRICING = {
  uk: { price: "£75", currency: "GBP", country: "United Kingdom" },
  us: { price: "$95", currency: "USD", country: "United States" },
  ca: { price: "CAD 120", currency: "CAD", country: "Canada" },
  au: { price: "AUD 130", currency: "AUD", country: "Australia" },
  ae: { price: "AED 350", currency: "AED", country: "United Arab Emirates" },
  sa: { price: "SAR 350", currency: "SAR", country: "Saudi Arabia" },
  in: { price: "₹5,000", currency: "INR", country: "India" },
};

const DEFAULT_PRICE_LINE = "₹5,000 / £75 / $95 / AED 350";

export default function SecondOpinion() {
  const [params] = useSearchParams();
  const initialCondition = CONDITIONS.find((c) => c.slug === params.get("condition"))?.name || "";
  const ref = (params.get("ref") || "").toLowerCase();
  const pricing = PRICING[ref];
  const priceLine = pricing ? pricing.price : DEFAULT_PRICE_LINE;
  const initialCountry = pricing?.country || "India";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: initialCountry,
    condition: initialCondition, diagnosis: "", imaging_notes: "", message: "", consent: false,
    campaign_ref: ref || null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please confirm consent to be contacted.");
    setSubmitting(true);
    try {
      await api.post("/second-opinion", { ...form, source: "second-opinion" });
      trackEvent("second_opinion_submitted", { country: form.country, condition: form.condition || "unspecified" });
      setDone(true);
      toast.success("Request received. Dr Sidhu's team will respond in writing within 2 working days.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="second-opinion-page" className="pt-28 md:pt-36 pb-28 min-h-screen">
      <SEO
        title="Second Opinion in 48 hours — written review by Dr Sidhu · Dr Tegbir Vascular"
        description="Get a written second opinion from Dr Tegbir Sidhu within 2 working days. Send your existing imaging and diagnosis; get a clear plan back before you commit to any procedure."
        keywords={["second opinion vascular india", "second opinion interventional radiology", "second opinion aortic aneurysm", "second opinion fibroid"]}
      />
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { label: "Second opinion" }]} />
          <div className="mt-8 kbd">— Second opinion in 48 hours</div>
          <h1 className="mt-6 font-serif text-6xl md:text-[7vw] leading-[0.96] tracking-tightest">
            <MaskedReveal text={"A written"}/>
            <MaskedReveal text={"second read,"} delay={0.1} className="italic font-normal"/>
            <MaskedReveal text={"in 48 hours."} delay={0.2}/>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">
            You've been told a diagnosis. You've been offered a procedure. Or you've been told "wait and watch." Before you decide — send it here. Dr Sidhu will personally review your imaging and the plan you've been given, and reply <span className="text-ink font-medium">in writing</span> within two working days.
          </p>
        </FadeUp>

        <div className="grid grid-cols-12 gap-8 mt-16">
          {/* Left: proposition */}
          <FadeUp className="col-span-12 md:col-span-5">
            <div className="hairline-t pt-8 space-y-8">
              <div className="flex items-start gap-4">
                <Clock size={20} className="text-crimson mt-1"/>
                <div>
                  <div className="kbd">2 working days</div>
                  <p className="mt-2 text-ink leading-relaxed">Written reply from Dr Sidhu — not a template, not an assistant. Reviewed on his consultation email, with imaging opened and read.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <FileText size={20} className="text-crimson mt-1"/>
                <div>
                  <div className="kbd">What you get</div>
                  <p className="mt-2 text-ink leading-relaxed">A one-page written opinion covering: is the diagnosis right, is the recommendation right, and what would a specialist do differently.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <ShieldCheck size={20} className="text-crimson mt-1"/>
                <div>
                  <div className="kbd">Fee & refund</div>
                  <p className="mt-2 text-ink leading-relaxed">Fixed practice fee: <span data-testid="so-price" className="font-medium">{priceLine}</span>. Fully <span className="font-medium">refundable</span> if you proceed to a full consultation or treatment with the practice.</p>
                </div>
              </div>
              <div className="hairline bg-white/60 p-5">
                <div className="kbd">Also fine</div>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">
                  If you decide our opinion is unhelpful, don't proceed. That's an acceptable outcome. This service exists to be useful — not to funnel you into treatment.
                </p>
              </div>
            </div>
          </FadeUp>

          {/* Right: form */}
          <FadeUp className="col-span-12 md:col-span-7" delay={0.15}>
            <div className="hairline p-6 md:p-10 bg-white/60 backdrop-blur">
              {done ? (
                <div data-testid="second-opinion-success" className="py-14 text-center">
                  <div className="kbd">— Received</div>
                  <h3 className="mt-6 font-serif text-4xl md:text-5xl tracking-tightest">Thank you, {form.name.split(" ")[0]}.</h3>
                  <p className="mt-6 text-ink-muted max-w-md mx-auto leading-relaxed">
                    Please send your imaging (CT, MRI, ultrasound reports and images) on WhatsApp now. Dr Sidhu will reply in writing to <span className="text-ink font-medium">{form.email}</span> within two working days.
                  </p>
                  <a target="_blank" rel="noreferrer" data-testid="second-opinion-whatsapp" href={WHATSAPP_LINK(`Hello, I just submitted a Second Opinion request (${form.name}, ${form.condition || 'condition not specified'}). I can share my imaging here.`)} className="btn-pill-inv mt-10 inline-flex">
                    Send imaging on WhatsApp <ArrowUpRight size={14}/>
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="second-opinion-form" className="grid grid-cols-2 gap-6">
                  <div className="col-span-2 md:col-span-1"><Label className="kbd">Name</Label><Input data-testid="so-name" required value={form.name} onChange={(e)=>update("name", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/></div>
                  <div className="col-span-2 md:col-span-1"><Label className="kbd">Email</Label><Input data-testid="so-email" type="email" required value={form.email} onChange={(e)=>update("email", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/></div>
                  <div className="col-span-2 md:col-span-1"><Label className="kbd">Phone / WhatsApp</Label><Input data-testid="so-phone" required value={form.phone} onChange={(e)=>update("phone", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/></div>
                  <div className="col-span-2 md:col-span-1">
                    <Label className="kbd">Country</Label>
                    <Select value={form.country} onValueChange={(v)=>update("country", v)}>
                      <SelectTrigger data-testid="so-country" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0"><SelectValue/></SelectTrigger>
                      <SelectContent>{COUNTRIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="kbd">Condition or concern</Label>
                    <Input data-testid="so-condition" value={form.condition} onChange={(e)=>update("condition", e.target.value)} placeholder="e.g., Uterine fibroid, PAD, aortic aneurysm — or 'not sure'" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/>
                  </div>
                  <div className="col-span-2">
                    <Label className="kbd">What you've been told so far</Label>
                    <Textarea data-testid="so-diagnosis" rows={3} value={form.diagnosis} onChange={(e)=>update("diagnosis", e.target.value)} placeholder="The diagnosis given, and the recommendation (surgery, watchful waiting, medication…). Include the specialty who advised (GP, gynae, surgeon)." className="mt-2 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0 resize-none"/>
                  </div>
                  <div className="col-span-2">
                    <Label className="kbd">Imaging you have</Label>
                    <Textarea data-testid="so-imaging" rows={2} value={form.imaging_notes} onChange={(e)=>update("imaging_notes", e.target.value)} placeholder="e.g., 'CT abdomen from 3 Nov', 'ultrasound + Doppler last week' — you'll send the files on WhatsApp after submitting." className="mt-2 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0 resize-none"/>
                  </div>
                  <div className="col-span-2 flex items-start gap-3 mt-2">
                    <Checkbox id="so-consent" data-testid="so-consent" checked={form.consent} onCheckedChange={(v)=>update("consent", Boolean(v))} className="mt-1"/>
                    <Label htmlFor="so-consent" className="text-sm text-ink-muted leading-relaxed">
                      I understand the second-opinion fee is <span className="text-ink">{priceLine}</span>, refundable against a full consultation or treatment. I consent to being contacted by Dr Sidhu's team.
                    </Label>
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center justify-between gap-4 mt-4">
                    <div className="kbd">Reply within 2 working days</div>
                    <button type="submit" data-testid="so-submit" disabled={submitting} className="btn-pill-inv disabled:opacity-60">
                      {submitting ? "Sending…" : "Request second opinion"} <ArrowUpRight size={14}/>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
